import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Modal, Card, Typography, Table, Tag, Space, Button, Splitter, Input, Tree, message } from 'antd'
import { CloseOutlined, CarOutlined, FileTextOutlined, BoxPlotOutlined, PlusOutlined, FolderOutlined, FileOutlined, SwapOutlined, ClockCircleOutlined, UserOutlined, OrderedListOutlined, ClusterOutlined, DeleteOutlined, ExclamationCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { lotesRelatorio } from '../../../services/api'
import { findOnboardMatch } from '../../../utils/dataUtils'
import { useNavigate } from 'react-router-dom'
import { abrirRelatorioLote } from '../../Relatorios/RelatorioLote'
import { useAuth } from '../../../contexts/AuthContext'

const { Title, Text } = Typography

// Estilo CSS para headers das tabelas e checkboxes
const tableHeaderStyle = `
  .custom-table-header .ant-table-thead > tr > th {
    font-size: 10px !important;
    font-family: Consolas, monospace !important;
  }
  
  /* Tabela de resumo por remetente: reduzir altura das linhas */
  .remetente-summary-table .ant-table-tbody > tr > td {
    padding: 0 6px !important;
    font-size: 8px !important;
    line-height: 1.2 !important;
  }
  .remetente-summary-table .ant-table-thead > tr > th {
    padding: 1px 6px !important;
    font-size: 8px !important;
  }
  .remetente-summary-table .ant-table-row.ant-table-row-level-0 > td {
    padding: 0 6px !important;
  }
  /* Garantir aplicação em células internas do Ant Design */
  .remetente-summary-table .ant-table-cell {
    padding: 0 6px !important;
  }
  
  .custom-checkbox {
    accent-color: #1890ff !important;
    background-color: #fff !important;
    border: 2px solid #d9d9d9 !important;
    border-radius: 3px !important;
    width: 18px !important;
    height: 18px !important;
    cursor: pointer !important;
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    position: relative !important;
  }
  
  .custom-checkbox:checked {
    background-color: #1890ff !important;
    border-color: #1890ff !important;
  }
  
  .custom-checkbox:checked::after {
    content: '✓' !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    color: white !important;
    font-size: 12px !important;
    font-weight: bold !important;
  }
  
  /* Estilos para tema dark */
  body[data-theme="dark"] .custom-checkbox,
  .ant-app[data-theme="dark"] .custom-checkbox,
  [data-theme="dark"] .custom-checkbox {
    background-color: #1f1f1f !important;
    border-color: #595959 !important;
  }
  
  body[data-theme="dark"] .custom-checkbox:checked,
  .ant-app[data-theme="dark"] .custom-checkbox:checked,
  [data-theme="dark"] .custom-checkbox:checked {
    background-color: #1890ff !important;
    border-color: #1890ff !important;
  }
  
  /* Fallback para temas escuros */
  @media (prefers-color-scheme: dark) {
    .custom-checkbox {
      background-color: #1f1f1f !important;
      border-color: #595959 !important;
    }
    
    .custom-checkbox:checked {
      background-color: #1890ff !important;
      border-color: #1890ff !important;
    }
  }

  /* Scrollbar visível para contêiner de Lotes salvos (tema light) */
  .lotes-salvos-scroll {
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: #69b1ff #e6f4ff; /* Firefox: thumb track */
  }
  .lotes-salvos-scroll::-webkit-scrollbar {
    height: 8px; /* Altura da barra horizontal */
  }
  .lotes-salvos-scroll::-webkit-scrollbar-track {
    background: #e6f4ff;
    border-radius: 8px;
  }
  .lotes-salvos-scroll::-webkit-scrollbar-thumb {
    background: #69b1ff;
    border-radius: 8px;
    border: 2px solid #e6f4ff; /* cria contraste no tema claro */
  }
  .lotes-salvos-scroll:hover::-webkit-scrollbar-thumb {
    background: #4096ff; /* mais visível ao passar o mouse */
  }

  /* Ajustes para tema dark */
  body[data-theme="dark"] .lotes-salvos-scroll {
    scrollbar-color: #4096ff #141414; /* Firefox */
  }
  body[data-theme="dark"] .lotes-salvos-scroll::-webkit-scrollbar-track {
    background: #141414;
  }
  body[data-theme="dark"] .lotes-salvos-scroll::-webkit-scrollbar-thumb {
    background: #4096ff;
    border-color: #141414;
  }
`

// Adicionar o estilo ao documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style')
  styleElement.textContent = tableHeaderStyle
  if (!document.head.querySelector('style[data-table-header]')) {
    styleElement.setAttribute('data-table-header', 'true')
    document.head.appendChild(styleElement)
  }
}

// Helpers para hierarquia de CTRC/CTE
const getCtrc = (item) => {
  const val = item?.cte || item?.ctrc || item?.CTE || item?.CTRC
  return (val && String(val).trim()) ? String(val).trim() : 'Sem CTRC'
}

const agruparPorCtrc = (itens = []) => {
  const grupos = {}
  itens.forEach(item => {
    const ctrc = getCtrc(item)
    if (!grupos[ctrc]) {
      grupos[ctrc] = []
    }
    grupos[ctrc].push(item)
  })
  return grupos
}

const ModalLotes = ({
  visible,
  onClose,
  carro,
  dadosCarroLotes = [],
  selectedDate = ''
}) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const empresaCodigo = user?.assignedBranch?.codigo ?? user?.empresa ?? ''
  const usuarioCodigo = user?.codigo ?? ''
  const [selectedItems, setSelectedItems] = useState([])
  const [selectedImpressos, setSelectedImpressos] = useState([])
  const [selectedImpressosChildren, setSelectedImpressosChildren] = useState({})
  const [selectedImpressosTreeKeys, setSelectedImpressosTreeKeys] = useState([])
  const [selectAllImpressos, setSelectAllImpressos] = useState(false)
  const [selectAllNaoImpressos, setSelectAllNaoImpressos] = useState(false)
  const [creatingLote, setCreatingLote] = useState(false)
  const [removedChaves, setRemovedChaves] = useState([])
  const [savedLotes, setSavedLotes] = useState([])

  // Ant Design v5: use scoped message instance to avoid static context warnings
  const [messageApi, messageContextHolder] = message.useMessage()

  // Ref para rolagem horizontal dos "Lotes salvos do carro"
  const savedLotesRef = useRef(null)

  const scrollSavedLotes = (direction = 1) => {
    const el = savedLotesRef.current
    if (!el) return
    const firstCard = el.querySelector('[data-salvo-card="true"]')
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 260
    el.scrollBy({ left: direction * (cardWidth + 8) * 2, behavior: 'smooth' })
  }

  const [loteDescricao, setLoteDescricao] = useState('')

  // Preview de lote (mostrar dados)
  const [lotePreviewVisible, setLotePreviewVisible] = useState(false)
  const [lotePreviewItens, setLotePreviewItens] = useState([])
  const [lotePreviewMeta, setLotePreviewMeta] = useState({ id: '', descricao: '', data_criacao: '', usuario: '', count: 0 })




  // padronizar as chaves para minúsculas
  const convertKeysToLowerCase = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => convertKeysToLowerCase(item))
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const lowerKey = key.toLowerCase()
        acc[lowerKey] = convertKeysToLowerCase(obj[key])
        return acc
      }, {})
    }
    return obj
  }

  // Processar dados recebidos para converter chaves para minúsculas
  // Garante array mesmo quando dadosCarroLotes é indefinido ou não-array
  const dadosProcessadosLowerCase = useMemo(() => {
    if (Array.isArray(dadosCarroLotes)) {
      return convertKeysToLowerCase(dadosCarroLotes)
    }
    return []
  }, [dadosCarroLotes])

  // Resolver data (yyyy-mm-dd): usar exclusivamente selectedDate, sem fallback para data atual
  const resolveDia = () => {
    try {
      if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        return selectedDate
      }
      return ''
    } catch {
      return ''
    }
  }

  useEffect(() => {
    const fetchSavedLotes = async () => {
      // Buscar lotes salvos para o carro após receber dadosCarroLotes/carro
      try {
        const dia = resolveDia()

        if (!dia) {
          return
        }

        if (!dadosCarroLotes || dadosCarroLotes.length === 0) {
          return
        }

        const primeiro = dadosCarroLotes[0]
        const carroAtual = carro || primeiro?.carro || primeiro?.Carro || ''
        const empresaAtual = primeiro?.empresa || primeiro?.filial || primeiro?.FILIAL || ''

        if (!carroAtual || !empresaAtual) {
          return
        }

        const resp = await lotesRelatorio.buscarPorCarro({
          carro: String(carroAtual),
          empresa: String(empresaAtual),
          data_criacao: String(dia)
        })

        const lotesNormalizados = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data) ? resp.data
            : Array.isArray(resp?.result) ? resp.result
              : (resp && typeof resp === 'object' ? [resp] : [])

        setSavedLotes(lotesNormalizados)
      } catch (err) {

      }
    }

    fetchSavedLotes()
  }, [dadosCarroLotes, carro, selectedDate])

  // Normalizar diferentes formatos de retorno em array
  const normalizeArray = (resp) => {
    return Array.isArray(resp)
      ? resp
      : Array.isArray(resp?.data) ? resp.data
        : Array.isArray(resp?.result) ? resp.result
          : (resp && typeof resp === 'object' ? [resp] : [])
  }

  // Conjunto memoizado de chaves já salvas para exclusão visual
  const savedSet = useMemo(() => {
    const top = (savedLotes || [])
      .map(item => item?.chave || item?.chave_nfe || item?.codigo || item?.CHAVE || item?.CHAVE_NFE || item?.CODIGO || '')
      .filter(Boolean)
    const nested = (savedLotes || [])
      .flatMap(item => {
        const itens = Array.isArray(item?.itens) ? item.itens : []
        return itens.map(it => it?.chave || it?.chave_nfe || it?.codigo || it?.CHAVE || it?.CHAVE_NFE || it?.CODIGO || '')
      })
      .filter(Boolean)
    return new Set([...top, ...nested])
  }, [savedLotes])

  // Reset selected items when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setSelectedImpressos([])
      setSelectedImpressosChildren({})
      setSelectedImpressosTreeKeys([])
      setSelectedItems([])
      setSelectAllImpressos(false)
      setSelectAllNaoImpressos(false)
    }
  }, [visible])



  // Funções para gerenciar seleções dos checkboxes da treeDataImpressos
  const handleParentImpressosChange = (parentKey, checked) => {
    if (checked) {
      setSelectedImpressos(prev => [...prev, parentKey])
      // Selecionar todos os filhos
      const parentData = dadosProcessadosLowerCase.find(item => item.cliente === parentKey)
      if (parentData && parentData.notas) {
        const childKeys = parentData.notas.map(nota => `${parentKey}-${nota.nota}`)
        setSelectedImpressosChildren(prev => ({
          ...prev,
          [parentKey]: childKeys
        }))
      }
    } else {
      setSelectedImpressos(prev => prev.filter(key => key !== parentKey))
      // Desselecionar todos os filhos
      setSelectedImpressosChildren(prev => {
        const newState = { ...prev }
        delete newState[parentKey]
        return newState
      })
    }
  }

  const handleChildImpressosChange = (parentKey, childKey, checked) => {
    setSelectedImpressosChildren(prev => {
      const parentChildren = prev[parentKey] || []
      let newParentChildren

      if (checked) {
        newParentChildren = [...new Set([...parentChildren, childKey])]
      } else {
        newParentChildren = parentChildren.filter(key => key !== childKey)
      }

      const newState = {
        ...prev,
        [parentKey]: newParentChildren
      }

      // Atualizar Tree com as chaves dos nós CTRC e do pai (Cliente), conforme seleção dos filhos
      setSelectedImpressosTreeKeys(prevTreeKeys => {
        const parentTreeKey = `1-${parentKey}`
        const parentGroup = gruposImpressos[parentKey]
        if (!parentGroup) return prevTreeKeys

        let next = [...prevTreeKeys]
        const ctrcGrupos = agruparPorCtrc(parentGroup)

        // Verificar cada CTRC deste cliente
        Object.keys(ctrcGrupos).forEach(ctrc => {
          const ctrcTreeKey = `1-${parentKey}__ctrc__${ctrc}`
          const itensCtrc = ctrcGrupos[ctrc]
          const ctrcChildKeys = itensCtrc.map(item => `impressos-${parentKey}-${item.seq}-${item.nota}`)
          const allCtrcSelected = ctrcChildKeys.length > 0 && ctrcChildKeys.every(k => newParentChildren.includes(k))

          next = next.filter(k => k !== ctrcTreeKey)
          if (allCtrcSelected) {
            next.push(ctrcTreeKey)
          }
        })

        // Verificar o pai (Cliente)
        const totalItems = parentGroup.length
        const selectedItemsInGroup = newParentChildren.length

        next = next.filter(k => k !== parentTreeKey)
        if (selectedItemsInGroup === totalItems && totalItems > 0) {
          next.push(parentTreeKey)
        }
        return [...new Set(next)]
      })

      // Atualizar seleção de pais conforme seleção total dos filhos
      setSelectedImpressos(prevParents => {
        const parentGroup = gruposImpressos[parentKey]
        if (!parentGroup) return prevParents
        const totalItems = parentGroup.length
        const selectedItemsInGroup = newParentChildren.length
        let nextParents = prevParents.filter(k => k !== parentKey)
        if (selectedItemsInGroup === totalItems && totalItems > 0) {
          nextParents = [...new Set([...nextParents, parentKey])]
        }
        return nextParents
      })

      // Se não há filhos selecionados, limpar o estado
      if (newParentChildren.length === 0) {
        delete newState[parentKey]
      }

      return newState
    })
  }

  // Função para selecionar todos os itens da treeDataImpressos
  const handleSelectAllImpressos = (checked) => {
    setSelectAllImpressos(checked)

    if (checked) {
      // Selecionar todos os pais
      const allParents = Object.keys(gruposImpressos)
      setSelectedImpressos(allParents)

      // Selecionar todos os filhos e chaves da árvore (clientes e CTRCs)
      const allChildren = {}
      const allTreeKeys = []

      allParents.forEach(parentKey => {
        allTreeKeys.push(`1-${parentKey}`)
        const grupo = gruposImpressos[parentKey]
        if (grupo && grupo.length > 0) {
          const childKeys = grupo.map(item => `impressos-${parentKey}-${item.seq}-${item.nota}`)
          allChildren[parentKey] = childKeys
          const ctrcGrupos = agruparPorCtrc(grupo)
          Object.keys(ctrcGrupos).forEach(ctrc => {
            allTreeKeys.push(`1-${parentKey}__ctrc__${ctrc}`)
          })
        }
      })

      setSelectedImpressosChildren(allChildren)
      setSelectedImpressosTreeKeys(allTreeKeys)
    } else {
      // Desselecionar todos
      setSelectedImpressos([])
      setSelectedImpressosChildren({})
      setSelectedImpressosTreeKeys([])
    }
  }

  // Função para selecionar todos os itens da treeDataNaoImpressos
  const handleSelectAllNaoImpressos = (checked) => {
    setSelectAllNaoImpressos(checked)

    if (checked) {
      // Selecionar todos os itens da tabela, grupos pais e nós CTRC
      const allItems = []
      Object.keys(gruposNaoImpressos).forEach(cnpj => {
        allItems.push(`0-${cnpj}`)

        const grupo = gruposNaoImpressos[cnpj]
        const ctrcGrupos = agruparPorCtrc(grupo)
        Object.keys(ctrcGrupos).forEach(ctrc => {
          allItems.push(`0-${cnpj}__ctrc__${ctrc}`)
        })

        grupo.forEach(item => {
          allItems.push(`nao-impressos-${cnpj}-${item.seq}-${item.nota}`)
        })
      })
      setSelectedItems(allItems)
    } else {
      // Desselecionar todos
      setSelectedItems([])
    }
  }

  const handleChildNaoImpressosChange = (cnpj, childKey, checked) => {
    setSelectedItems(prev => {
      let next = checked ? [...new Set([...prev, childKey])] : prev.filter(k => k !== childKey)
      const parentGroup = gruposNaoImpressos[cnpj] || []
      const parentKey = `0-${cnpj}`
      const ctrcGrupos = agruparPorCtrc(parentGroup)

      // Verificar cada CTRC deste cliente
      Object.keys(ctrcGrupos).forEach(ctrc => {
        const ctrcTreeKey = `0-${cnpj}__ctrc__${ctrc}`
        const itensCtrc = ctrcGrupos[ctrc]
        const ctrcChildKeys = itensCtrc.map(item => `nao-impressos-${cnpj}-${item.seq}-${item.nota}`)
        const allCtrcSelected = ctrcChildKeys.length > 0 && ctrcChildKeys.every(k => next.includes(k))

        next = next.filter(k => k !== ctrcTreeKey)
        if (allCtrcSelected) {
          next.push(ctrcTreeKey)
        }
      })

      // Verificar o pai (Cliente)
      const totalItems = parentGroup.length
      const selectedCount = next.filter(k => typeof k === 'string' && k.startsWith(`nao-impressos-${cnpj}-`)).length
      next = next.filter(k => k !== parentKey)
      if (selectedCount === totalItems && totalItems > 0) {
        next.push(parentKey)
      }
      return [...new Set(next)]
    })
  }

  // Colunas para a tabela de itens do carro
  const columns = [
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 120,
      ellipsis: true,
      render: (text) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: 'CD',
      dataIndex: 'cd',
      key: 'cd',
      width: 60,
      render: (text) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: 'Nota',
      dataIndex: 'nota',
      key: 'nota',
      width: 80,
      render: (text) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const color = status === 'IMPRESSO' ? 'green' : 'orange';
        return <Tag color={color} style={{ fontSize: '10px', padding: '2px 6px' }}>{status}</Tag>;
      },
    },
    {
      title: 'Data',
      dataIndex: 'data_criacao',
      key: 'data',
      width: 90,
      render: (date) => (
        <span style={{ fontSize: '11px' }}>
          {date ? new Date(date).toLocaleDateString('pt-BR') : '-'}
        </span>
      ),
    },
  ]


  // Separação e processamento dos dados (otimizado com useMemo)
  const dadosProcessados = useMemo(() => {
    // Filtrar itens removidos (pós-Criação de lote) por chave normalizada
    const removedSet = new Set((removedChaves || []).filter(Boolean))
    const base = Array.isArray(dadosProcessadosLowerCase) ? dadosProcessadosLowerCase : []

    const blockedSet = new Set([...removedSet, ...savedSet])

    const dadosBase = blockedSet.size > 0
      ? base.filter(item => {
        const chave = item.chave || item.chave_nfe || item.codigo || ''
        const isBlocked = blockedSet.has(chave)
        return !isBlocked
      })
      : base


    // Separar dados por status
    const dadosImpressos = dadosBase.filter(item => item.status === 1)
    const dadosNaoImpressos = dadosBase.filter(item => item.status === 0)

    // Função para processar e agrupar dados impressos (ordenação por data_etiqueta)
    const processarDadosImpressos = (dados) => {
      // Agrupar por cnpj
      const grupos = {}

      dados.forEach(item => {
        const cnpj = item.cnpj_remetente || item.remetente || 'Sem CNPJ'

        if (!grupos[cnpj]) {
          grupos[cnpj] = []
        }

        grupos[cnpj].push(item)
      })

      // Processar cada grupo: ordenar por data_etiqueta e adicionar sequência
      Object.keys(grupos).forEach(cnpj => {
        // Ordenar por data_etiqueta em ordem crescente
        grupos[cnpj].sort((a, b) => {
          const dataA = new Date(a.data_etiqueta || a.data || a.data_criacao || 0)
          const dataB = new Date(b.data_etiqueta || b.data || b.data_criacao || 0)
          return dataA - dataB
        })

        // Adicionar sequência sequencial dentro do cliente (iniciando em 1)
        grupos[cnpj] = grupos[cnpj].map((item, index) => ({
          ...item,
          seq: index + 1
        }))
      })

      return grupos
    }

    // Função para processar e agrupar dados não impressos (ordenação por data)
    const processarDadosNaoImpressos = (dados) => {
      // Agrupar por cnpj
      const grupos = {}

      dados.forEach(item => {
        const cnpj = item.cnpj_remetente || item.remetente || 'Sem CNPJ'

        if (!grupos[cnpj]) {
          grupos[cnpj] = []
        }

        grupos[cnpj].push(item)
      })

      // Processar cada grupo: ordenar por data_etiqueta e adicionar sequência
      Object.keys(grupos).forEach(cnpj => {
        // Ordenar por data_etiqueta em ordem crescente
        grupos[cnpj].sort((a, b) => {
          const dataA = new Date(a.data_etiqueta || a.data || a.data_criacao || 0)
          const dataB = new Date(b.data_etiqueta || b.data || b.data_criacao || 0)
          return dataA - dataB
        })

        // Adicionar sequência sequencial dentro do cliente (iniciando em 1)
        grupos[cnpj] = grupos[cnpj].map((item, index) => ({
          ...item,
          seq: index + 1
        }))
      })

      return grupos
    }

    const gruposImpressos = processarDadosImpressos(dadosImpressos)
    const gruposNaoImpressos = processarDadosNaoImpressos(dadosNaoImpressos)

    // Calcular estatísticas baseadas no agrupamento por nota de cada cliente
    const calcularEstatisticas = (grupos) => {
      let totalNotas = 0
      Object.values(grupos).forEach(grupo => {
        // Contar notas únicas por cliente
        const notasUnicas = new Set(grupo.map(item => item.nota))
        totalNotas += notasUnicas.size
      })
      return totalNotas
    }

    const totalItens = dadosBase.length
    const itensImpressos = calcularEstatisticas(gruposImpressos)
    const itensPendentes = calcularEstatisticas(gruposNaoImpressos)

    return {
      gruposImpressos,
      gruposNaoImpressos,
      estatisticas: { totalItens, itensImpressos, itensPendentes }
    }
  }, [dadosProcessadosLowerCase, removedChaves, savedSet])

  const { gruposImpressos, gruposNaoImpressos, estatisticas } = dadosProcessados
  const { totalItens, itensImpressos, itensPendentes } = estatisticas

  // Contadores derivados para resumo da seleção
  const selectedImpressosCount = useMemo(() => {
    return Object.values(selectedImpressosChildren).reduce((acc, arr) => acc + (arr?.length || 0), 0)
  }, [selectedImpressosChildren])

  const selectedNaoImpressosCount = useMemo(() => {
    return selectedItems.filter(key => typeof key === 'string' && key.startsWith('nao-impressos-')).length
  }, [selectedItems])

  const selectedTotalCount = selectedImpressosCount + selectedNaoImpressosCount

  const totalImpressosCount = useMemo(() => {
    return Object.values(gruposImpressos).reduce((acc, grupo) => acc + (grupo?.length || 0), 0)
  }, [gruposImpressos])

  const totalNaoImpressosCount = useMemo(() => {
    return Object.values(gruposNaoImpressos).reduce((acc, grupo) => acc + (grupo?.length || 0), 0)
  }, [gruposNaoImpressos])

  const totalAvailableCount = totalImpressosCount + totalNaoImpressosCount

  const generateLoteId = () => {
    const now = new Date()
    const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`
    const base = `REL-${carro || 'SEM'}-${stamp}`
    return base.slice(0, 50)
  }

  const normalizeChave = (it) => it?.chave || it?.chave_nfe || it?.codigo || ''
  const extractNrColeta = (it) => it?.nrcoleta || it?.coleta || it?.romaneio || ''

  const collectSelectedItems = () => {
    const itens = []
    Object.entries(selectedImpressosChildren || {}).forEach(([cnpj, keys]) => {
      const group = gruposImpressos[cnpj] || []
        ; (keys || []).forEach(k => {
          const parts = String(k).split('-')
          const seq = parts[parts.length - 2]
          const item = group.find(x => String(x.seq) === String(seq))
          if (item) itens.push(item)
        })
    })
    selectedItems
      .filter(k => typeof k === 'string' && k.startsWith('nao-impressos-'))
      .forEach(k => {
        const parts = String(k).split('-')
        const cnpj = parts[2]
        const seq = parts[3]
        const group = gruposNaoImpressos[cnpj] || []
        const item = group.find(x => String(x.seq) === String(seq))
        if (item) itens.push(item)
      })

    // Enriquecer itens com todos os campos necessários da estrutura completa
    const itensEnriquecidos = itens.map(item => {
      // Garantir que todos os campos necessários estejam presentes
      return {
        ...item,
        // Campos básicos já existentes
        id: item.id,
        lote_id: item.lote_id,
        chave: item.chave || item.chave_nfe || item.codigo || item.CHAVE || '',
        carro: item.carro || item.CARRO || '',
        nrcoleta: item.nrcoleta || item.coleta || item.COLETA || item.romaneio || item.ROMANEIO || '999999',
        empresa: item.empresa || item.filial || item.FILIAL || 0,
        usuario: item.usuario,
        descricao: item.descricao || '',
        data_criacao: item.data_criacao || item.data || item.DATA || '',

        // Todos os campos da estrutura completa
        FILIAL: item.FILIAL || item.filial || item.empresa || 0,
        CARRO: item.CARRO || item.carro || '',
        COLETA: item.COLETA || item.coleta || item.nrcoleta || 0,
        ROMANEIO: item.ROMANEIO || item.romaneio || 0,
        CNPJ: item.CNPJ || item.cnpj || item.cnpj_remetente || '',
        REMETENTE: item.REMETENTE || item.remetente || item.cliente || '',
        NOTA: item.NOTA || item.nota || 0,
        SERIE: item.SERIE || item.serie || '',
        VOLUMES: item.VOLUMES || item.volumes || item.volumesoriginal || 0,
        PESO: item.PESO || item.peso || 0,
        M3: item.M3 || item.m3 || 0,
        CEPENTREGA: item.CEPENTREGA || item.cepentrega || item.cep_entrega || '',
        codigo: item.codigo || item.CODIGO || '',
        DATA: item.DATA || item.data || item.data_criacao || '',
        STATUS: item.STATUS || item.status || 0,
        natureza: item.natureza || item.NATUREZA || null,
        STATUS_COLETA: item.STATUS_COLETA || item.status_coleta || '',
        cddestinatario: item.cddestinatario || item.CDDESTINATARIO || '',
        destinatario: item.destinatario || item.DESTINATARIO || '',
        cidade: item.cidade || item.CIDADE || '',
        praca: item.praca || item.PRACA || '',
        rota: item.rota || item.ROTA || '',
        cd: item.cd || item.CD || '',
        uf: item.uf || item.UF || '',
        pedido: item.pedido || item.PEDIDO || '',
        nrtransporte: item.nrtransporte || item.NRTRANSPORTE || '',
        nridentificacaocarga: item.nridentificacaocarga || item.NRIDENTIFICACAOCARGA || '0',
        sigla: item.sigla || item.SIGLA || 0,
        obs: item.obs || item.OBS || '',
        inclusao: item.inclusao || item.INCLUSAO || 'LEITURA',
        frete: item.frete || item.FRETE || 0,
        volumesoriginal: item.volumesoriginal || item.VOLUMESORIGINAL || item.VOLUMES || item.volumes || 0,
        TTL_ETIQUETAS_IMP: item.TTL_ETIQUETAS_IMP || item.ttl_etiquetas_imp || 0,
        TTL_ETIQUETAS_SEM: item.TTL_ETIQUETAS_SEM || item.ttl_etiquetas_sem || 0,
        PRIMEIRA_ETIQUETA: item.PRIMEIRA_ETIQUETA || item.primeira_etiqueta || '',
        ULTIMA_ETIQUETA: item.ULTIMA_ETIQUETA || item.ultima_etiqueta || '',
        impressora: item.impressora || item.IMPRESSORA || null,
        CLIENTE: item.CLIENTE || item.cliente || item.REMETENTE || item.remetente || '',
        CD: item.CD || item.cd || '',
        Praca: item.Praca || item.praca || item.PRACA || '',
        STATUS_ETIQUETA: item.STATUS_ETIQUETA || item.status_etiqueta || 0,
        DATA_ETIQUETA: item.DATA_ETIQUETA || item.data_etiqueta || '',

        // Campos em lowercase para compatibilidade
        nota: item.nota || item.NOTA || 0,
        serie: item.serie || item.SERIE || '',
        peso: item.peso || item.PESO || 0,
        m3: item.m3 || item.M3 || 0,
        cepentrega: item.cepentrega || item.CEPENTREGA || item.cep_entrega || '',
        remetente: item.remetente || item.REMETENTE || item.dsremetente || item.cliente || '',
        cnpj: item.cnpj || item.CNPJ || item.cnpj_remetente || '',
        cnpj_remetente: item.cnpj_remetente || item.cnpj || item.CNPJ || item.cdremetente || '',
        cte: item.cte || item.ctrc || item.CTE || item.CTRC || '',
        ctrc: item.ctrc || item.cte || item.CTRC || item.CTE || '',

        // Campo para alterações
        alteracoes: item.alteracoes || item.ALTERACOES || ''
      }
    })


    return itensEnriquecidos
  }

  const handleCriarLote = async () => {
    if (creatingLote) return
    const itensSel = collectSelectedItems()
    if (!itensSel || itensSel.length === 0) {
      messageApi.warning('Selecione ao menos um item')
      return
    }
    if (!loteDescricao || !String(loteDescricao).trim()) {
      messageApi.error('Informe o nome do lote')
      return
    }
    setCreatingLote(true)
    try {
      const dia = resolveDia()
      if (!dia) {
        messageApi.warning('Selecione a data (selectedDate) antes de criar o lote')
        setCreatingLote(false)
        return
      }
      const payload = {
        lote_id: generateLoteId(),
        data_criacao: dia,
        descricao: String(loteDescricao).trim(),
        empresa: String(empresaCodigo || ''),
        usuario: String(usuarioCodigo || ''),
        itens: itensSel.map(it => ({
          chave: normalizeChave(it),
          carro: carro || it?.carro || '',
          nrcoleta: extractNrColeta(it) || '',
          empresa: empresaCodigo || ''
        }))
      }
      const resp = await lotesRelatorio.criar(payload)
      messageApi.success(`Lote criado: ${payload.lote_id} (${resp?.itens_inseridos || itensSel.length} itens)`)
      // Remover itens selecionados das visualizações (Tree/Tabelas)
      const novasChavesRemover = (itensSel || []).map(it => normalizeChave(it)).filter(Boolean)
      if (novasChavesRemover.length > 0) {
        setRemovedChaves(prev => [...new Set([...(prev || []), ...novasChavesRemover])])
      }
      setSelectedItems([])
      setSelectedImpressos([])
      setSelectedImpressosChildren({})
      setSelectedImpressosTreeKeys([])

      // Abrir relatório em nova aba
      try {
        const primeiro = Array.isArray(dadosCarroLotes) && dadosCarroLotes.length > 0 ? dadosCarroLotes[0] : {}
        const carroAtual = carro || primeiro?.carro || primeiro?.Carro || ''
        const empresaAtual = empresaCodigo || primeiro?.empresa || primeiro?.filial || primeiro?.FILIAL || ''


        abrirRelatorioLote({
          loteId: payload.lote_id,
          meta: {
            lote_id: payload.lote_id,
            data_criacao: dia,
            descricao: payload.descricao,
            carro: carroAtual,
            empresa: String(empresaAtual || ''),
            usuario: String(usuarioCodigo || (user?.nome || user?.username || 'Usuário'))
          },
          itens: itensSel,
          user: user
        })
      } catch (navErr) {
        console.error('Erro ao abrir relatório:', navErr)
        messageApi.error('Erro ao abrir relatório')
      }

      // Atualizar badges: reconsultar lotes salvos para refletir recém-criado
      try {
        const dia = resolveDia()

        if (!dia) {
          return
        }

        // Verificar se dados existem e têm pelo menos um item
        if (!dadosCarroLotes || dadosCarroLotes.length === 0) {
          return
        }

        const primeiro = dadosCarroLotes[0]
        const carroAtual = carro || primeiro?.carro || primeiro?.Carro || ''
        const nrcoletaAtual = primeiro?.coleta || primeiro?.nrcoleta || primeiro?.NrColeta || '999999'
        const empresaAtual = empresaCodigo || primeiro?.empresa || primeiro?.filial || primeiro?.FILIAL || ''

        // Verificar se os valores estão vazios
        if (!carroAtual) {

        }

        const resp2 = await lotesRelatorio.buscarPorCarro({
          carro: String(carroAtual),
          empresa: String(empresaAtual),
          data_criacao: String(dia)
        })

        // Normalizar possíveis formatos de resposta
        const lotesNormalizados = Array.isArray(resp2)
          ? resp2
          : Array.isArray(resp2?.data) ? resp2.data
            : Array.isArray(resp2?.result) ? resp2.result
              : (resp2 && typeof resp2 === 'object' ? [resp2] : [])

        setSavedLotes(lotesNormalizados)
      } catch (err2) {

      }
    } catch (e) {
      messageApi.error(`Erro ao criar lote: ${e.message || e}`)
    } finally {
      setCreatingLote(false)
    }
  }

  // Agrupar notas selecionadas por remetente (cliente)
  const selecionadosPorRemetente = useMemo(() => {
    const mapa = new Map()
    // Impressos: usar selectedImpressosChildren por cnpj
    Object.entries(selectedImpressosChildren || {}).forEach(([cnpj, keys]) => {
      if (!keys || keys.length === 0) return
      if (!mapa.has(cnpj)) mapa.set(cnpj, new Set())
      const setNotas = mapa.get(cnpj)
      keys.forEach(k => {
        // k: impressos-{cnpj}-{seq}-{nota} → nota é último segmento
        const parts = String(k).split('-')
        const nota = parts[parts.length - 1]
        setNotas.add(nota)
      })
    })

    // Não Impressos: filtrar selectedItems por cnpj e coletar notas
    Object.keys(gruposNaoImpressos || {}).forEach(cnpj => {
      const childKeys = selectedItems.filter(k => typeof k === 'string' && k.startsWith(`nao-impressos-${cnpj}-`))
      if (childKeys.length === 0) return
      if (!mapa.has(cnpj)) mapa.set(cnpj, new Set())
      const setNotas = mapa.get(cnpj)
      childKeys.forEach(k => {
        const parts = String(k).split('-')
        const nota = parts[parts.length - 1]
        setNotas.add(nota)
      })
    })

    // Converter para array de linhas
    const linhas = Array.from(mapa.entries()).map(([cnpj, notasSet]) => ({
      key: `sel-${cnpj}`,
      remetente: cnpj,
      notasSelecionadas: (notasSet?.size || 0)
    }))

    // Ordenar por quantidade desc, depois alfabética
    linhas.sort((a, b) => {
      if (b.notasSelecionadas !== a.notasSelecionadas) return b.notasSelecionadas - a.notasSelecionadas
      return String(a.remetente).localeCompare(String(b.remetente))
    })

    return linhas
  }, [selectedImpressosChildren, selectedItems, gruposNaoImpressos])

  // Sincronizar selectAllNaoImpressos com selectedItems
  useEffect(() => {
    if (Object.keys(gruposNaoImpressos).length > 0) {
      // Calcular total de itens disponíveis
      const totalItems = Object.keys(gruposNaoImpressos).reduce((total, cnpj) => {
        return total + gruposNaoImpressos[cnpj].length
      }, 0)

      // Verificar se todos os itens estão selecionados
      const selectedChildCount = selectedItems.filter(key => typeof key === 'string' && key.startsWith('nao-impressos-')).length
      const allSelected = totalItems > 0 && selectedChildCount === totalItems

      // Atualizar o estado apenas se necessário para evitar loops
      if (selectAllNaoImpressos !== allSelected) {
        setSelectAllNaoImpressos(allSelected)
      }
    }
  }, [selectedItems, gruposNaoImpressos, selectAllNaoImpressos])

  // useEffect para sincronizar selectAllImpressos com selectedImpressosTreeKeys
  useEffect(() => {
    if (gruposImpressos && Object.keys(gruposImpressos).length > 0) {
      const totalParents = Object.keys(gruposImpressos).length
      const totalChildren = Object.values(gruposImpressos).reduce((acc, grupo) => acc + grupo.length, 0)
      const selectedParents = selectedImpressos.length
      const selectedChildren = Object.values(selectedImpressosChildren).reduce((acc, arr) => acc + (arr?.length || 0), 0)

      const allSelected = totalParents > 0 && totalChildren > 0 &&
        selectedParents === totalParents && selectedChildren === totalChildren

      if (selectAllImpressos !== allSelected) {
        setSelectAllImpressos(allSelected)
      }
    }
  }, [selectedImpressos, selectedImpressosChildren, gruposImpressos, selectAllImpressos])



  // TreeData para impressos com hierarquia Cliente -> Ctrc -> Notas
  const treeDataImpressos = useMemo(() => {
    return Object.keys(gruposImpressos).map(cnpj => {
      const grupo = gruposImpressos[cnpj]
      const notasUnicas = new Set(grupo.map(item => item.nota))
      const ctrcGrupos = agruparPorCtrc(grupo)

      const childrenCtrc = Object.keys(ctrcGrupos).map(ctrc => {
        const itensCtrc = ctrcGrupos[ctrc]
        const notasCtrc = new Set(itensCtrc.map(item => item.nota))

        return {
          title: (
            <Space>
              <FileTextOutlined style={{ color: '#13c2c2' }} />
              <Text strong style={{ fontSize: '11px', fontFamily: 'Consolas, monospace' }}>
                CTRC: {ctrc}
              </Text>
              <Tag size="small" color={ctrc === 'Sem CTRC' ? 'default' : 'cyan'}>
                {notasCtrc.size} {notasCtrc.size === 1 ? 'nota' : 'notas'}
              </Tag>
              <Tag size="small" color="blue">
                {itensCtrc.length} {itensCtrc.length === 1 ? 'item' : 'itens'}
              </Tag>
            </Space>
          ),
          key: `1-${cnpj}__ctrc__${ctrc}`,
          children: [{
            title: (
              <Table
                size="small"
                pagination={false}
                showHeader={true}
                style={{ fontFamily: 'Consolas, monospace', fontSize: '8px' }}
                className="custom-table-header"
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: '',
                    dataIndex: 'checkbox',
                    key: 'checkbox',
                    width: 40,
                    render: (_, record) => (
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={selectedImpressosChildren[cnpj]?.includes(record.key) || false}
                        onChange={(e) => {
                          handleChildImpressosChange(cnpj, record.key, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )
                  },
                  {
                    title: 'Seq',
                    dataIndex: 'seq',
                    key: 'seq',
                    width: 50,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Nota',
                    dataIndex: 'nota',
                    key: 'nota',
                    width: 100,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'CTRC',
                    dataIndex: 'ctrc',
                    key: 'ctrc',
                    width: 90,
                    render: (text) => <span style={{ fontSize: '8px', color: '#13c2c2' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Nr1',
                    dataIndex: 'nr1',
                    key: 'nr1',
                    width: 80,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Nr2',
                    dataIndex: 'nr2',
                    key: 'nr2',
                    width: 80,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Destinatário',
                    dataIndex: 'destinatario',
                    key: 'destinatario',
                    width: 200,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Cidade',
                    dataIndex: 'cidade',
                    key: 'cidade',
                    width: 120,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'UF',
                    dataIndex: 'uf',
                    key: 'uf',
                    width: 40,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Vol(s)',
                    dataIndex: 'volumes',
                    key: 'volumes',
                    width: 60,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Chave',
                    dataIndex: 'chave_nfe',
                    key: 'chave_nfe',
                    width: 150,
                    render: (text) => (
                      <Text type="secondary" style={{ fontSize: '8px' }}>
                        {text || 'N/A'}
                      </Text>
                    )
                  }
                ]}
                dataSource={itensCtrc.map(item => ({
                  key: `impressos-${cnpj}-${item.seq}-${item.nota}`,
                  seq: item.seq,
                  nota: item.nota || 'N/A',
                  ctrc: item.cte || item.ctrc || 'N/A',
                  nr1: item.nr1 || 'N/A',
                  nr2: item.nr2 || 'N/A',
                  destinatario: item.destinatario || item.cliente || 'N/A',
                  cidade: item.cidade || 'N/A',
                  uf: item.uf || 'N/A',
                  volumes: item.volumes || item.vol || 'N/A',
                  chave_nfe: item.chave_nfe || item.chave || 'N/A'
                }))}
              />
            ),
            key: `1-${cnpj}__ctrc__${ctrc}-table`,
            icon: <FileOutlined style={{ color: '#52c41a' }} />,
            isLeaf: true,
            disableCheckbox: true
          }]
        }
      })

      return {
        title: (
          <Space>
            <FolderOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: '12px', fontFamily: 'Consolas, monospace' }}>{cnpj}</Text>
            <Tag size="small" color="green">{notasUnicas.size} notas</Tag>
            <Tag size="small" color="blue">{grupo.length} itens</Tag>
          </Space>
        ),
        key: `1-${cnpj}`,
        children: childrenCtrc
      }
    })
  }, [gruposImpressos, selectedImpressosChildren])

  // TreeData para não impressos com hierarquia Cliente -> Ctrc -> Notas
  const treeDataNaoImpressos = useMemo(() => {
    return Object.keys(gruposNaoImpressos).map(cnpj => {
      const grupo = gruposNaoImpressos[cnpj]
      const notasUnicas = new Set(grupo.map(item => item.nota))
      const ctrcGrupos = agruparPorCtrc(grupo)

      const childrenCtrc = Object.keys(ctrcGrupos).map(ctrc => {
        const itensCtrc = ctrcGrupos[ctrc]
        const notasCtrc = new Set(itensCtrc.map(item => item.nota))

        return {
          title: (
            <Space>
              <FileTextOutlined style={{ color: '#13c2c2' }} />
              <Text strong style={{ fontSize: '11px', fontFamily: 'Consolas, monospace' }}>
                CTRC: {ctrc}
              </Text>
              <Tag size="small" color={ctrc === 'Sem CTRC' ? 'default' : 'cyan'}>
                {notasCtrc.size} {notasCtrc.size === 1 ? 'nota' : 'notas'}
              </Tag>
              <Tag size="small" color="blue">
                {itensCtrc.length} {itensCtrc.length === 1 ? 'item' : 'itens'}
              </Tag>
            </Space>
          ),
          key: `0-${cnpj}__ctrc__${ctrc}`,
          children: [{
            title: (
              <Table
                size="small"
                pagination={false}
                showHeader={true}
                style={{ fontFamily: 'Consolas, monospace', fontSize: '8px' }}
                className="custom-table-header"
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: '',
                    dataIndex: 'checkbox',
                    key: 'checkbox',
                    width: 40,
                    render: (_, record) => (
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={selectedItems.includes(record.key)}
                        onChange={(e) => handleChildNaoImpressosChange(cnpj, record.key, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )
                  },
                  {
                    title: 'Seq',
                    dataIndex: 'seq',
                    key: 'seq',
                    width: 50,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Nota',
                    dataIndex: 'nota',
                    key: 'nota',
                    width: 100,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'CTRC',
                    dataIndex: 'ctrc',
                    key: 'ctrc',
                    width: 90,
                    render: (text) => <span style={{ fontSize: '8px', color: '#13c2c2' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Nr1',
                    dataIndex: 'nr1',
                    key: 'nr1',
                    width: 80,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Nr2',
                    dataIndex: 'nr2',
                    key: 'nr2',
                    width: 80,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Destinatário',
                    dataIndex: 'destinatario',
                    key: 'destinatario',
                    width: 200,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Cidade',
                    dataIndex: 'cidade',
                    key: 'cidade',
                    width: 120,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'UF',
                    dataIndex: 'uf',
                    key: 'uf',
                    width: 40,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Vol(s)',
                    dataIndex: 'volumes',
                    key: 'volumes',
                    width: 60,
                    render: (text) => <span style={{ fontSize: '8px' }}>{text}</span>
                  },
                  {
                    title: 'Chave',
                    dataIndex: 'chave_nfe',
                    key: 'chave_nfe',
                    width: 150,
                    render: (text) => (
                      <Text type="secondary" style={{ fontSize: '8px' }}>
                        {text || 'N/A'}
                      </Text>
                    )
                  }
                ]}
                dataSource={itensCtrc.map(item => ({
                  key: `nao-impressos-${cnpj}-${item.seq}-${item.nota}`,
                  seq: item.seq,
                  nota: item.nota || 'N/A',
                  ctrc: item.cte || item.ctrc || 'N/A',
                  nr1: item.nr1 || 'N/A',
                  nr2: item.nr2 || 'N/A',
                  destinatario: item.destinatario || item.cliente || 'N/A',
                  cidade: item.cidade || 'N/A',
                  uf: item.uf || 'N/A',
                  volumes: item.volumes || item.vol || 'N/A',
                  chave_nfe: item.chave_nfe || item.chave || 'N/A'
                }))}
              />
            ),
            key: `0-${cnpj}__ctrc__${ctrc}-table`,
            icon: <FileOutlined style={{ color: '#faad14' }} />,
            isLeaf: true,
            disableCheckbox: true
          }]
        }
      })

      return {
        title: (
          <Space>
            <FolderOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: '12px', fontFamily: 'Consolas, monospace' }}>{cnpj}</Text>
            <Tag size="small" color="orange">{notasUnicas.size} notas</Tag>
            <Tag size="small" color="blue">{grupo.length} itens</Tag>
          </Space>
        ),
        key: `0-${cnpj}`,
        children: childrenCtrc
      }
    })
  }, [gruposNaoImpressos, selectedItems])

  return (
    <>
      {messageContextHolder}
      <Modal
        title={null}
        open={visible}
        onCancel={onClose}
        footer={null}
        width="100vw"
        style={{
          top: 0,
          padding: 0,
          maxWidth: 'none'
        }}
        styles={{
          body: {
            height: '100vh',
            padding: '12px',
            overflow: 'hidden'
          }
        }}
        closable={false}
        maskClosable={false}
      >
        {/* Header da Modal */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '6px'
        }}>
          <Space size="small">
            <CarOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0, fontSize: '16px' }}>
              Gestão de Lotes - {carro || 'Carro não identificado'}
            </Title>
          </Space>

          <Space size="small">
            <Text style={{ fontSize: '12px' }}>Total: <strong>{totalItens}</strong></Text>
            <Text style={{ fontSize: '12px' }}>Impressos: <strong style={{ color: '#52c41a' }}>{itensImpressos}</strong></Text>
            <Text style={{ fontSize: '12px' }}>Pendentes: <strong style={{ color: '#fa8c16' }}>{itensPendentes}</strong></Text>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              size="small"
              style={{ fontSize: '14px' }}
            />
          </Space>
        </div>

        {/* Conteúdo Principal com Splitter */}
        <div style={{ height: 'calc(100vh - 80px)' }}>
          <Splitter
            style={{ height: '100%', boxShadow: '0 0 6px rgba(0, 0, 0, 0.1)' }}
          >
            {/* Painel Esquerdo - Lista de Itens */}
            <Splitter.Panel defaultSize="60%" min="40%" max="80%">
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>

                {/* Card Impressos */}
                <Card
                  title={
                    <Space size="small">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={selectAllImpressos}
                        onChange={(e) => handleSelectAllImpressos(e.target.checked)}
                        style={{ marginRight: '4px' }}
                      />
                      <span style={{ fontSize: '14px' }}>Impressos</span>
                      <Tag color="green" style={{ fontSize: '11px' }}>
                        {itensImpressos} itens
                      </Tag>
                    </Space>
                  }
                  style={{ height: '50%', minHeight: '200px' }}
                  styles={{
                    header: { padding: '6px 12px', minHeight: '35px' },
                    body: {
                      height: 'calc(100% - 35px)',
                      overflow: 'auto',
                      padding: '6px'
                    }
                  }}
                >
                  <Tree
                    treeData={treeDataImpressos}
                    defaultExpandAll={false}
                    showIcon={true}
                    style={{ fontSize: '12px' }}
                    height={200}
                    checkable={true}
                    checkedKeys={selectedImpressosTreeKeys}
                    onCheck={(checkedKeys, info) => {
                      const nodeKey = info?.node?.key
                      const isChecked = info?.checked

                      if (typeof nodeKey === 'string' && nodeKey.startsWith('1-')) {
                        if (nodeKey.includes('__ctrc__')) {
                          // Clicou no nó CTRC
                          const [cnpj, ctrc] = nodeKey.replace('1-', '').split('__ctrc__')
                          const parentGroup = gruposImpressos[cnpj] || []
                          const ctrcGrupos = agruparPorCtrc(parentGroup)
                          const itensCtrc = ctrcGrupos[ctrc] || []
                          const ctrcChildKeys = itensCtrc.map(item => `impressos-${cnpj}-${item.seq}-${item.nota}`)

                          setSelectedImpressosChildren(prev => {
                            const current = prev[cnpj] || []
                            let nextChildren
                            if (isChecked) {
                              nextChildren = [...new Set([...current, ...ctrcChildKeys])]
                            } else {
                              nextChildren = current.filter(k => !ctrcChildKeys.includes(k))
                            }
                            const newState = { ...prev }
                            if (nextChildren.length > 0) {
                              newState[cnpj] = nextChildren
                            } else {
                              delete newState[cnpj]
                            }

                            // Sincronizar tree keys e selectedImpressos
                            setSelectedImpressosTreeKeys(prevTree => {
                              let nextTree = prevTree.filter(k => k !== nodeKey)
                              if (isChecked) nextTree.push(nodeKey)

                              const parentTreeKey = `1-${cnpj}`
                              nextTree = nextTree.filter(k => k !== parentTreeKey)
                              if (nextChildren.length === parentGroup.length && parentGroup.length > 0) {
                                nextTree.push(parentTreeKey)
                              }
                              return [...new Set(nextTree)]
                            })

                            setSelectedImpressos(prevParents => {
                              let nextParents = prevParents.filter(k => k !== cnpj)
                              if (nextChildren.length === parentGroup.length && parentGroup.length > 0) {
                                nextParents.push(cnpj)
                              }
                              return [...new Set(nextParents)]
                            })

                            return newState
                          })
                        } else {
                          // Clicou no nó Cliente (pai)
                          const cnpj = nodeKey.replace('1-', '')
                          const grupo = gruposImpressos[cnpj] || []
                          const ctrcGrupos = agruparPorCtrc(grupo)
                          const ctrcKeys = Object.keys(ctrcGrupos).map(ctrc => `1-${cnpj}__ctrc__${ctrc}`)
                          const childKeys = grupo.map(item => `impressos-${cnpj}-${item.seq}-${item.nota}`)

                          setSelectedImpressosTreeKeys(prev => {
                            let next = prev.filter(k => k !== nodeKey && !ctrcKeys.includes(k))
                            if (isChecked) next = [...new Set([...next, nodeKey, ...ctrcKeys])]
                            return next
                          })

                          setSelectedImpressos(prev => {
                            let next = prev.filter(k => k !== cnpj)
                            if (isChecked) next = [...new Set([...next, cnpj])]
                            return next
                          })

                          setSelectedImpressosChildren(prev => {
                            const next = { ...prev }
                            if (isChecked) {
                              next[cnpj] = childKeys
                            } else {
                              delete next[cnpj]
                            }
                            return next
                          })
                        }
                      } else {
                        setSelectedImpressosTreeKeys(checkedKeys)
                      }
                    }}
                  />
                </Card>

                {/* Card Não Impressos */}
                <Card
                  title={
                    <Space size="small">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={selectAllNaoImpressos}
                        onChange={(e) => handleSelectAllNaoImpressos(e.target.checked)}
                        style={{ marginRight: '4px' }}
                      />
                      <span style={{ fontSize: '14px' }}>Não Impressos</span>
                      <Tag color="orange" style={{ fontSize: '11px' }}>
                        {itensPendentes} itens
                      </Tag>
                      {selectedItems.length > 0 && (
                        <Tag color="blue" style={{ fontSize: '11px' }}>{selectedItems.length} selecionados</Tag>
                      )}
                    </Space>
                  }
                  style={{ height: '50%', minHeight: '200px' }}
                  styles={{
                    header: { padding: '6px 12px', minHeight: '35px' },
                    body: {
                      height: 'calc(100% - 35px)',
                      overflow: 'auto',
                      padding: '6px'
                    }
                  }}
                >
                  <Tree
                    treeData={treeDataNaoImpressos}
                    defaultExpandAll={false}
                    showIcon={true}
                    style={{ fontSize: '12px' }}
                    height={200}
                    checkable={true}
                    checkedKeys={selectedItems}
                    onCheck={(checkedKeys, info) => {
                      const nodeKey = info?.node?.key
                      const isChecked = info?.checked
                      if (typeof nodeKey === 'string' && nodeKey.startsWith('0-')) {
                        if (nodeKey.includes('__ctrc__')) {
                          // Clicou no nó CTRC
                          const [cnpj, ctrc] = nodeKey.replace('0-', '').split('__ctrc__')
                          const parentGroup = gruposNaoImpressos[cnpj] || []
                          const ctrcGrupos = agruparPorCtrc(parentGroup)
                          const itensCtrc = ctrcGrupos[ctrc] || []
                          const ctrcChildKeys = itensCtrc.map(item => `nao-impressos-${cnpj}-${item.seq}-${item.nota}`)

                          setSelectedItems(prev => {
                            let next = prev.filter(k => k !== nodeKey && !ctrcChildKeys.includes(k))
                            if (isChecked) {
                              next = [...next, nodeKey, ...ctrcChildKeys]
                            }

                            const parentKey = `0-${cnpj}`
                            const totalItems = parentGroup.length
                            const selectedCount = next.filter(k => typeof k === 'string' && k.startsWith(`nao-impressos-${cnpj}-`)).length
                            next = next.filter(k => k !== parentKey)
                            if (selectedCount === totalItems && totalItems > 0) {
                              next.push(parentKey)
                            }
                            return [...new Set(next)]
                          })
                        } else {
                          // Clicou no nó Cliente (pai)
                          const cnpj = nodeKey.replace('0-', '')
                          const grupo = gruposNaoImpressos[cnpj] || []
                          const ctrcGrupos = agruparPorCtrc(grupo)
                          const ctrcKeys = Object.keys(ctrcGrupos).map(ctrc => `0-${cnpj}__ctrc__${ctrc}`)
                          const itemKeys = grupo.map(item => `nao-impressos-${cnpj}-${item.seq}-${item.nota}`)

                          setSelectedItems(prev => {
                            let next = prev.filter(k => !k.startsWith(`nao-impressos-${cnpj}-`) && !ctrcKeys.includes(k) && k !== nodeKey)
                            if (isChecked) {
                              next = [...new Set([...next, nodeKey, ...ctrcKeys, ...itemKeys])]
                            }
                            return next
                          })
                        }
                      } else {
                        setSelectedItems(checkedKeys)
                      }
                    }}
                  />
                </Card>

              </div>
            </Splitter.Panel>

            {/* Painel Direito - Criação de Lotes */}
            <Splitter.Panel>
              {/* Lotes já criados para o carro (badges) */}
              {Array.isArray(savedLotes) && savedLotes.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <Title level={5} style={{ fontSize: '12px', margin: '0 0 6px' }}>Lotes salvos do carro</Title>
                  <div className="lotes-salvos-scroll" style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', overflowX: 'auto', paddingBottom: '6px', width: '100%', WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory' }}>
                    {(() => {
                      // Resumo por lote_id: descricao, count, data_criacao, usuario
                      const mapa = new Map()
                      savedLotes.forEach(it => {
                        const idRaw = it?.lote_id || it?.lote || it?.id || it?.descricao || 'Lote'
                        const id = String(idRaw)
                        const desc = it?.descricao || '—'
                        const obs = it?.observacoes || it?.descricao || '—'
                        const usuario = it?.usuario || ''
                        const dataRaw = it?.data_criacao || ''
                        let dataFmt = ''
                        try {
                          if (typeof dataRaw === 'string' && dataRaw) {
                            // tentar extrair AAAA-MM-DD
                            const m = dataRaw.match(/\d{4}-\d{2}-\d{2}/)
                            dataFmt = m ? m[0] : ''
                          } else if (dataRaw instanceof Date) {
                            const y = dataRaw.getFullYear()
                            const mo = String(dataRaw.getMonth() + 1).padStart(2, '0')
                            const d = String(dataRaw.getDate()).padStart(2, '0')
                            dataFmt = `${y}-${mo}-${d}`
                          }
                        } catch { }

                        const atual = mapa.get(id) || { observacoes: obs, descricao: desc, count: 0, data_criacao: dataFmt, usuario }
                        atual.count += 1
                        // manter primeira data/usuario se ainda vazio
                        if (!atual.data_criacao && dataFmt) atual.data_criacao = dataFmt
                        if (!atual.usuario && usuario) atual.usuario = usuario
                        if (!atual.observacoes && obs) atual.observacoes = obs
                        mapa.set(id, atual)
                      })
                      const linhas = Array.from(mapa.entries()).map(([id, info]) => ({ id, ...info }))
                      // Ordenar por maior quantidade
                      linhas.sort((a, b) => b.count - a.count)
                      return linhas.map(row => {
                        const onDelete = () => {
                          Modal.confirm({
                            title: 'Excluir lote?',
                            icon: <ExclamationCircleOutlined />,
                            content: `O lote ${row.id}${row.descricao && row.descricao !== '—' ? ` (${row.descricao})` : ''} será removido. Essa ação não pode ser desfeita.`,
                            okText: 'Excluir',
                            cancelText: 'Cancelar',
                            okButtonProps: { danger: true },
                            onOk: async () => {
                              try {
                                await lotesRelatorio.excluir(row.id)
                                setSavedLotes(prev => prev.filter(it => {
                                  const ident = it?.lote_id || it?.lote || it?.id
                                  return String(ident) !== String(row.id)
                                }))
                                messageApi.success('Lote excluído com sucesso')
                              } catch (e) {
                                messageApi.error(`Erro ao excluir lote: ${e.message || e}`)
                              }
                            }
                          })
                        }
                        return (
                          <div key={`lote-salvo-${row.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', border: '1px solid #e6f4ff', background: '#fff', borderRadius: '8px', padding: '8px 10px', flex: '0 0 auto', scrollSnapAlign: 'start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: '#1f1f1f' }}>{row.observacoes || row.descricao || '—'}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#595959', fontSize: '12px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <CarOutlined /> {String(carro || (Array.isArray(dadosCarroLotes) && (dadosCarroLotes[0]?.CARRO || dadosCarroLotes[0]?.carro)) || '—')}
                                </span>
                                <span style={{ height: '12px', width: '1px', background: '#d9d9d9' }} />
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <UserOutlined /> {row.usuario || '—'}
                                </span>
                                <span style={{ height: '12px', width: '1px', background: '#d9d9d9' }} />
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <OrderedListOutlined /> {row.count} itens
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Button
                                onClick={async () => {
                                  try {
                                    // Usar itens já carregados em savedLotes, filtrando pelo lote_id
                                    const carroAtual = carro || (Array.isArray(dadosCarroLotes) && dadosCarroLotes[0]?.CARRO) || ''
                                    const registros = (savedLotes || []).filter(it => {
                                      const ident = it?.lote_id || it?.lote || it?.id || it?.descricao
                                      return String(ident) === String(row.id)
                                    })

                                    const itensFonte = registros.flatMap(it => Array.isArray(it?.itens) ? it.itens : [it])

                                    const itensEnriquecidos = itensFonte.map(it => ({
                                      ...it,
                                      NOTA: it?.NOTA || it?.nota || 0,
                                      SERIE: it?.SERIE || it?.serie || '',
                                      CNPJ: it?.CNPJ || it?.cnpj || it?.cnpj_remetente || it?.CNPJ_REMETENTE || '',
                                      cnpj: it?.cnpj || it?.CNPJ || it?.cnpj_remetente || it?.CNPJ_REMETENTE || '',
                                      remetente: it?.remetente || it?.REMETENTE || it?.remetente_nome || it?.REMETENTE_NOME || '',
                                      chave_nfe: it?.chave_nfe || it?.chave || it?.CODIGO || it?.codigo || '',
                                      nr1: it?.nr1 || '',
                                      nr2: it?.nr2 || '',
                                      destinatario: it?.destinatario || it?.DESTINATARIO || it?.cliente || '',
                                      cidade: it?.cidade || it?.CIDADE || '',
                                      uf: it?.uf || it?.UF || '',
                                      CEPENTREGA: it?.CEPENTREGA || it?.cepentrega || '',
                                      VOLUMES: it?.volumesoriginal || it?.VOLUMES || it?.volumes || it?.vol || 0,
                                      PESO: it?.PESO || it?.peso || 0,
                                      M3: it?.M3 || it?.m3 || 0,
                                      STATUS: it?.STATUS || it?.status || ''
                                    }))

                                    abrirRelatorioLote({
                                      loteId: row.id,
                                      meta: {
                                        lote_id: row.id,
                                        descricao: row.observacoes || row.descricao,
                                        carro: carroAtual,
                                        data_criacao: resolveDia(),
                                        empresa: String(empresaCodigo || ''),
                                        usuario: String(usuarioCodigo || (user?.nome || user?.username || 'Usuário'))
                                      },
                                      itens: itensEnriquecidos,
                                      user: user
                                    })
                                  } catch (e) {
                                    messageApi.error(`Erro ao montar itens do lote: ${e.message || e}`)
                                  }
                                }}
                                style={{ width: '36px', height: '28px', border: '1px solid #722ed1', color: '#722ed1', background: '#fff', borderRadius: '6px', padding: 0 }}
                                icon={<ClusterOutlined />}
                              />
                              <Button onClick={onDelete} style={{ width: '36px', height: '28px', border: '1px solid #ff4d4f', color: '#ff4d4f', background: '#fff', borderRadius: '6px', padding: 0 }} icon={<DeleteOutlined />} />
                            </div>
                          </div>
                        )
                      })
                    })()}
                  </div>
                </div>
              )}

              {/* criar lotes */}
              <Card
                title={
                  <Space size="small">
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      backgroundColor: '#1890ff',
                      borderRadius: '3px'
                    }}>
                      <BoxPlotOutlined style={{ color: 'white', fontSize: '12px' }} />
                    </div>
                    <span style={{ fontSize: '14px' }}>Criar Lote</span>
                  </Space>
                }
                style={{ height: '100%' }}
                styles={{
                  header: { padding: '8px 12px', minHeight: '40px' },
                  body: {
                    height: 'calc(100% - 40px)',
                    overflow: 'hidden',
                    padding: '12px'
                  }
                }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>


                  {/* dividir em dois */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'start' }}>
                    {/* Informações do Lote */}
                    <div>
                      <Title level={5} style={{ fontSize: '14px', marginBottom: '8px' }}>Informações do Lote</Title>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div>
                          <Text strong style={{ fontSize: '12px' }}>Nome do Lote:</Text>
                          <Input
                            placeholder="Digite o nome do lote"
                            size="small"
                            value={loteDescricao}
                            onChange={(e) => setLoteDescricao(e.target.value)}
                            maxLength={100}
                            style={{ marginTop: '4px' }}
                          />
                        </div>
                      </Space>
                    </div>

                    {/* Resumo da Seleção */}
                    <div>
                      <Title level={5} style={{ fontSize: '14px', marginBottom: '8px' }}>Resumo da Seleção</Title>
                      <Card size="small" style={{ backgroundColor: '#f9f9f9' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: '12px' }}>Itens selecionados:</Text>
                            <Text strong style={{ fontSize: '12px' }}>{selectedTotalCount}</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: '12px' }}>Total de itens:</Text>
                            <Text strong style={{ fontSize: '12px' }}>{totalAvailableCount}</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: '12px' }}>Percentual:</Text>
                            <Text strong style={{ fontSize: '12px' }}>
                              {totalAvailableCount > 0
                                ? `${((selectedTotalCount / totalAvailableCount) * 100).toFixed(1)}%`
                                : '0%'
                              }
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    </div>

                    {/* Seleção por Remetente */}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Title level={5} style={{ fontSize: '12px', margin: '1px 0 8px' }}>Notas selecionadas por Remetente</Title>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      <Table
                        name="table-selecao"
                        size="small"
                        pagination={false}
                        showHeader={true}
                        className="remetente-summary-table custom-table-header"
                        scroll={{ x: 'max-content', y: 240 }}
                        style={{ fontFamily: 'Consolas, monospace', fontSize: '10px' }}
                        columns={[
                          { title: 'Remetente', dataIndex: 'remetente', key: 'remetente', render: (text) => <span style={{ fontSize: '11px' }}>{text}</span> },
                          { title: 'Notas selecionadas', dataIndex: 'notasSelecionadas', key: 'notasSelecionadas', width: 140, render: (n) => <span style={{ fontSize: '11px' }}>{n}</span> }
                        ]}
                        dataSource={selecionadosPorRemetente}
                      />
                    </div>
                  </div>

                  {/* Ações */}
                  <div style={{ marginTop: 'auto' }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Button
                        name="btn-criar-lote"
                        type="primary"
                        size="small"
                        block
                        disabled={selectedTotalCount === 0 || creatingLote}
                        loading={creatingLote}
                        onClick={handleCriarLote}
                        icon={<PlusOutlined />}
                        style={{ fontSize: '12px' }}
                      >
                        Criar Lote ({selectedTotalCount} itens)
                      </Button>

                      <Button
                        block
                        size="small"
                        onClick={() => setSelectedItems([])}
                        disabled={selectedItems.length === 0}
                        style={{ fontSize: '12px' }}
                      >
                        Limpar Seleção
                      </Button>
                    </Space>
                  </div>
                </Space>
              </Card>
            </Splitter.Panel>
          </Splitter>
        </div>

        {/* Modal de preview dos itens do lote */}
        <Modal
          title={`Lote ${lotePreviewMeta.id}${lotePreviewMeta.descricao && lotePreviewMeta.descricao !== '—' ? ` (${lotePreviewMeta.descricao})` : ''}`}
          open={lotePreviewVisible}
          onCancel={() => setLotePreviewVisible(false)}
          footer={null}
          width={900}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#595959', fontSize: '12px', marginBottom: '8px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <ClockCircleOutlined /> {lotePreviewMeta.data_criacao || '—'}
            </span>
            <span style={{ height: '12px', width: '1px', background: '#d9d9d9' }} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <UserOutlined /> {lotePreviewMeta.usuario || '—'}
            </span>
            <span style={{ height: '12px', width: '1px', background: '#d9d9d9' }} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <OrderedListOutlined /> {lotePreviewItens?.length || 0} itens
            </span>
          </div>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 'max-content', y: 300 }}
            columns={[
              { title: 'Seq', dataIndex: 'seq', key: 'seq', width: 60, render: (text) => <span style={{ fontSize: '10px' }}>{text}</span> },
              { title: 'Nota', dataIndex: 'nota', key: 'nota', width: 100, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Nr1', dataIndex: 'nr1', key: 'nr1', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Nr2', dataIndex: 'nr2', key: 'nr2', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Destinatário', dataIndex: 'destinatario', key: 'destinatario', width: 200, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Cidade', dataIndex: 'cidade', key: 'cidade', width: 140, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'UF', dataIndex: 'uf', key: 'uf', width: 50, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Vol(s)', dataIndex: 'volumes', key: 'volumes', width: 80, render: (text) => <span style={{ fontSize: '10px' }}>{text || 'N/A'}</span> },
              { title: 'Chave', dataIndex: 'chave_nfe', key: 'chave_nfe', width: 220, render: (text) => <Text type="secondary" style={{ fontSize: '10px' }}>{text || 'N/A'}</Text> }
            ]}
            dataSource={(Array.isArray(lotePreviewItens) ? lotePreviewItens : []).map(item => ({
              key: `prev-${item.seq || item.nota || item.chave || Math.random()}`,
              seq: item.seq,
              nota: item.nota || 'N/A',
              nr1: item.nr1 || 'N/A',
              nr2: item.nr2 || 'N/A',
              destinatario: item.destinatario || item.cliente || 'N/A',
              cidade: item.cidade || 'N/A',
              uf: item.uf || 'N/A',
              volumes: item.volumes || item.vol || 'N/A',
              chave_nfe: item.chave_nfe || item.chave || 'N/A'
            }))}
          />
        </Modal>
      </Modal>
    </>
  )
}

export default ModalLotes
